import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { DeltaStatic, Sources } from 'quill';
import 'quill-mention';
import ReactQuill, { ReactQuillProps } from 'react-quill';

import { media } from 'context';
import Layout from './Layout';
import Text from './Text';

export interface Props extends Pick<
  ReactQuillProps,
  'onChange' | 'value' | 'modules' | 'formats' | 'style' | 'readOnly' | 'placeholder' | 'onFocus' | 'onBlur'
> {
  maxLength?: number;
  onTextLengthChange?: (length: number) => void;
  hideToolbar?: boolean;
  autoFocus?: boolean;
  helpTextList?: string[];
  alwaysShowHelpText?: boolean;
}

const BASE_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link']
  ]
};

const FORMATS = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'indent', 'mention'];

const RichTextEditor = React.forwardRef<ReactQuill, Props>(
  (
    {
      value,
      onChange,
      modules,
      formats,
      readOnly,
      placeholder,
      onFocus,
      onBlur,
      maxLength,
      onTextLengthChange,
      hideToolbar,
      autoFocus,
      helpTextList,
      alwaysShowHelpText
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef<string>('');
    const internalRef = useRef<ReactQuill>(null);
    const { isMobile } = useContext(media);

    // Use provided ref or internal ref
    const quillRef = (ref as React.RefObject<ReactQuill>) || internalRef;

    // Build effective modules based on props
    const effectiveModules = { ...BASE_MODULES, ...modules };

    // quill was firing onChange events on mount, which is some BS, and was creating problems in form state
    const handleChange = (
      newValue: string,
      delta: DeltaStatic,
      source: Sources,
      editor: ReactQuill.UnprivilegedEditor
    ) => {
      // Convert to string and only call onChange if the value has actually changed
      const stringValue = String(newValue || '') as string;

      // Get the actual text length from the editor (includes newlines, excludes formatting)
      const textLength = editor.getLength() - 1; // Subtract 1 because Quill always adds a trailing newline

      // If maxLength is set and text exceeds it, prevent the change
      if (maxLength && textLength > maxLength) {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const selection = quill.getSelection();
          const cursorPosition = selection ? selection.index : 0;

          // Revert to the last valid value
          quill.setContents(quill.clipboard.convert(lastValueRef.current));

          // Restore cursor position after Quill finishes processing
          setTimeout(() => {
            try {
              const validPosition = Math.min(Math.max(0, cursorPosition - 1), quill.getLength() - 1);
              quill.setSelection(validPosition, 0);
            } catch (error) {
              // Silently handle any range errors
            }
          }, 0);
        }
        return;
      }

      if (stringValue !== lastValueRef.current) {
        lastValueRef.current = stringValue;
        onChange?.(stringValue, delta, source, editor);
        onTextLengthChange?.(textLength);
      }
    };

    // Update lastValueRef when value prop changes externally
    useEffect(() => {
      const stringValue = String(value || '');
      lastValueRef.current = stringValue;
    }, [value]);

    useEffect(() => {
      const toolbar = containerRef.current?.querySelector<HTMLDivElement>('.ql-toolbar');
      if (!toolbar) return;
      if (hideToolbar) {
        toolbar.style.display = 'none';
      } else {
        toolbar.style.display = '';
      }
    }, [hideToolbar]);

    useEffect(() => {
      if (!autoFocus) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      editor.focus();
      const length = editor.getLength();
      editor.setSelection(length, length);
    }, [autoFocus, quillRef]);

    return (
      <div className={classNames('RichTextEditor', { 'is-focused': isFocused })} ref={containerRef}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={effectiveModules}
          formats={formats || FORMATS}
          bounds=".RichTextEditor"
          readOnly={readOnly}
          placeholder={placeholder}
          onFocus={(...args) => {
            setIsFocused(true);
            onFocus?.(...args);
          }}
          onBlur={(quill, source, ...rest) => {
            const activeElement = document.activeElement;

            if (containerRef?.current?.contains(activeElement)) {
              return;
            }

            setIsFocused(false);
            onBlur?.(quill, source, ...rest);
          }}
        />
        {(isFocused || alwaysShowHelpText) && helpTextList && helpTextList.length > 0 && (
          <Layout paddingTop={8} className="RichTextEditor-helpTextList">
            <ul className="RichTextEditor-helpTextList-ul">
              {helpTextList.map((item, index) => (
                <li key={index} className="RichTextEditor-helpTextList-li">
                  <Text size={isMobile ? 'body-sm' : 'body-xs'} color="gray-600">
                    {item}
                  </Text>
                </li>
              ))}
            </ul>
          </Layout>
        )}
      </div>
    );
  }
);

export default RichTextEditor;
